import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ArrowRight, Clock, Brain, TrendingUp, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const benefits = [
  "ton score d'efficacite entrepreneuriale",
  "ton score de liberte entrepreneuriale",
  "le temps que tu perds chaque semaine",
  "comment ton entreprise pourrait fonctionner differemment",
];

export default function DiagnosticSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-premium bg-gradient-to-br from-slate-50 via-blue-50/30 to-white overflow-hidden"
      data-testid="diagnostic-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 badge-premium mb-6">
              <ClipboardCheck className="w-4 h-4" />
              Outil gratuit
            </span>
            
            <h2 className="heading-section mb-6">
              Faites le diagnostic de{' '}
              <span className="text-gradient">ton entreprise</span>
            </h2>
            
            <p className="body-large mb-6">
              En quelques minutes, decouvrez :
            </p>

            <div className="space-y-4 mb-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-ba-success flex-shrink-0" />
                  <span className="text-slate-600">{benefit}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-slate-500 mb-10 leading-relaxed">
              De nombreux entrepreneurs travaillent enormement mais leur entreprise depend entierement d'eux. 
              Ce diagnostic te permet d'analyser ton organisation et de decouvrir comment gagner du temps, 
              reduire ta charge mentale et mieux structurer ton entreprise.
            </p>

            <Link to="/diagnostic-entrepreneur">
              <Button 
                className="w-full sm:w-auto bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-10 py-7 text-lg btn-premium group"
                data-testid="diagnostic-cta"
              >
                Faire mon diagnostic (3 minutes)
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>

          {/* Right - Visual Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-100">
              {/* Header */}
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-ba-primary/10 rounded-2xl flex items-center justify-center">
                  <ClipboardCheck className="w-7 h-7 text-ba-primary" />
                </div>
                <div>
                  <p className="font-bold text-slate-800 text-lg">Diagnostic Entrepreneur</p>
                  <p className="text-slate-500">Analyse complete en 3 min</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-slate-500 mb-3">
                  <span className="font-medium">Progression</span>
                  <span>6 etapes</span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={isInView ? { width: "66%" } : {}}
                    transition={{ delay: 0.5, duration: 1.2, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-ba-primary to-ba-accent rounded-full"
                  />
                </div>
              </div>

              {/* Scores */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-slate-50 rounded-2xl p-5 text-center border border-slate-100">
                  <TrendingUp className="w-6 h-6 text-ba-primary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-ba-primary">--</p>
                  <p className="text-sm text-slate-500 font-medium">Score efficacite</p>
                </div>
                <div className="bg-slate-50 rounded-2xl p-5 text-center border border-slate-100">
                  <Brain className="w-6 h-6 text-ba-secondary mx-auto mb-2" />
                  <p className="text-3xl font-bold text-ba-secondary">--</p>
                  <p className="text-sm text-slate-500 font-medium">Score liberte</p>
                </div>
              </div>

              {/* Categories */}
              <div className="space-y-3">
                {['Administratif', 'Relation client', 'Prospection', 'Marketing', 'Organisation', 'Charge mentale'].map((cat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${i < 4 ? 'bg-ba-success' : 'bg-slate-300'}`} />
                    <span className={`text-sm font-medium ${i < 4 ? 'text-slate-700' : 'text-slate-400'}`}>{cat}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              className="absolute -bottom-4 -left-4 bg-ba-success text-white rounded-xl px-5 py-3 shadow-lg flex items-center gap-2"
            >
              <Clock className="w-5 h-5" />
              <span className="font-bold">3 min</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
